// Session-cookie auth state (10-security.md #5). GET /account both
// confirms the session and returns the profile; a 401 anywhere (via
// api/client.ts's onUnauthorized hook) clears the user and lets routing
// redirect to /login (docs/plan/phase-10-ui.md "handle 401 by redirecting
// to login").
import { forgetAllSessions } from "../lib/genSession";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { getAccount, login as apiLogin, logout as apiLogout, onUnauthorized, type Account } from "../api/client";

interface AuthState {
  user: Account | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const account = await getAccount();
      setUser(account);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    onUnauthorized(() => setUser(null));
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    await apiLogin(email, password);
    await refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      // The Generate session (photos, prompt, runs) must not outlive the
      // user at a shared browser.
      forgetAllSessions();
      setUser(null);
    }
  }, []);

  return <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
